import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';


const UNIVERSITY_CATEGORIES = [
  "Road/Pathway Issue","Library Issue","Hostel Issue","Ground/Maintenance",
  "Security Issue","Electricity/Water","Cafeteria/Food","Transport/Shuttle",
  "Medical/Health","Sports Facility","Other (Please specify)"
];

function CreateComplaintInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const { user } = useAuth();
  const userDepartment = user?.department || '';
  const [step, setStep] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [complaintType, setComplaintType] = useState('department');
  const [formData, setFormData] = useState({ title: '', description: '', universityCategory: '', otherCategory: '' });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const inputStyle = {
    ...s.input,
  };

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); setError(''); };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const combined = [...images, ...newFiles];
    if (combined.length > 5) { setError('Maximum 5 files allowed'); return; }
    const valid = [], newPreviews = [];
    for (const f of newFiles) {
      const isImage = f.type.startsWith('image/');
      const isVideo = f.type.startsWith('video/');
      if (!isImage && !isVideo) { setError('Only images and videos allowed'); return; }
      if (f.size > 50 * 1024 * 1024) { setError('Max 50MB per file'); return; }
      valid.push(f);
      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, { type: 'image', src: reader.result }]);
        };
        reader.readAsDataURL(f);
      } else {
        setImagePreviews(prev => [...prev, { type: 'video', src: URL.createObjectURL(f), name: f.name }]);
      }
    }
    setImages(prev => [...prev, ...valid]);
    setError('');
    // reset input so same file can be re-added if removed
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (complaintType === 'department' && !userDepartment) { setError('Your account has no department assigned'); return; }
    if (complaintType === 'university' && !formData.universityCategory) { setError('Select a category'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('complaint_type', complaintType);
      if (complaintType === 'department') fd.append('department', userDepartment);
      else {
        let cat = formData.universityCategory;
        if (cat === 'Other (Please specify)') cat = formData.otherCategory;
        fd.append('university_category', cat);
      }
      images.forEach(img => fd.append('images', img));
      await api.post('/complaints/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Complaint submitted successfully!');
      setTimeout(() => navigate('/complaints'), 1800);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const typeButtonStyle = (active) => ({
    flex: 1, padding: '14px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13,
    border: active ? `1px solid rgba(255,255,255,0.45)` : '1px solid rgba(255,255,255,0.2)',
    background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
    color: '#fff',
    transition: 'all 0.2s ease',
  });

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="New Complaint"
            subtitle="Register a new complaint"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/complaints')}>Cancel</button>
            }
          />
          <div style={s.content}>
            <div style={{ maxWidth: 680, margin: '0 auto' }}>
              <PageHeader
                icon="✍️"
                title="New Complaint"
                subtitle="Register a new complaint in 2 steps"
                stats={[{ label: 'Step', value: `${step} of 2` }]}
              />
              {/* Progress */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {[1, 2].map(n => (
                    <div key={n} style={{ flex: 1, height: 4, borderRadius: 2,
                      background: step >= n
                        ? 'linear-gradient(90deg,#667eea,#764ba2)'
                        : 'rgba(255,255,255,0.15)',
                      transition: 'background 0.3s ease',
                    }} />
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  {['Complaint Details', 'Category & Images'].map((label, i) => (
                    <span key={label} style={{ fontSize: 12, color: step >= i + 1 ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: step >= i + 1 ? 600 : 400 }}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              <div style={s.card}>
                {error && <div style={s.alertErr}>{error}</div>}
                {success && <div style={s.alertOk}>{success}</div>}

                <form onSubmit={handleSubmit}>
                  {step === 1 && (
                    <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
                      <div style={{ ...s.sectionTitle, marginBottom: 20 }}>Complaint Details</div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={s.label}>Title</label>
                        <input name="title" value={formData.title} onChange={handleChange} placeholder="Brief title of your complaint" maxLength={200} style={inputStyle} />
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textAlign: 'right', marginTop: 4 }}>{formData.title.length}/200</div>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <label style={s.label}>Description</label>
                        <textarea name="description" value={formData.description} onChange={handleChange} rows={6} placeholder="Describe your complaint in detail..." maxLength={2000}
                          style={{ ...s.textarea }} />
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', textAlign: 'right', marginTop: 4 }}>{formData.description.length}/2000</div>
                      </div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <button type="button" className="ur-btn-hover" style={{ ...s.btnSecondary, flex: 1 }} onClick={() => navigate('/complaints')}>Cancel</button>
                        <button type="button" className="ur-btn-hover" style={{ ...s.btnPrimary, flex: 2 }}
                          onClick={() => { if (!formData.title.trim()) { setError('Title required'); return; } if (!formData.description.trim()) { setError('Description required'); return; } setError(''); setStep(2); }}>
                          Next Step
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
                      <div style={{ ...s.sectionTitle, marginBottom: 20 }}>Category & Attachments</div>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                        <button type="button" style={typeButtonStyle(complaintType === 'department')} onClick={() => setComplaintType('department')}>Department Issue</button>
                        <button type="button" style={typeButtonStyle(complaintType === 'university')} onClick={() => setComplaintType('university')}>University Issue</button>
                      </div>

                      {complaintType === 'department' && (
                        <div style={{ marginBottom: 16 }}>
                          <label style={s.label}>Department</label>
                          <div style={{
                            ...s.input,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            cursor: 'default',
                          }}>
                            <span style={{ color: userDepartment ? '#fff' : 'rgba(255,255,255,0.45)' }}>
                              {userDepartment || 'No department on your account'}
                            </span>
                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginLeft: 8 }}>auto-filled</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                            Complaints are filed under your registered department.
                          </div>
                        </div>
                      )}

                      {complaintType === 'university' && (
                        <>
                          <div style={{ marginBottom: 16 }}>
                            <label style={s.label}>Issue Category</label>
                            <select name="universityCategory" value={formData.universityCategory} onChange={handleChange} style={s.select}>
                              <option value="">Select Category</option>
                              {UNIVERSITY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          {formData.universityCategory === 'Other (Please specify)' && (
                            <div style={{ marginBottom: 16 }}>
                              <label style={s.label}>Please Specify</label>
                              <input name="otherCategory" value={formData.otherCategory} onChange={handleChange} placeholder="Enter category" style={inputStyle} />
                            </div>
                          )}
                        </>
                      )}

                      {/* Image Upload */}
                      <div style={{ marginBottom: 20 }}>
                        <label style={s.label}>Attach Images (Optional)</label>
                        <label htmlFor="file-upload" style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '28px', borderRadius: 10, cursor: 'pointer',
                          border: '2px dashed rgba(255,255,255,0.25)',
                          background: 'rgba(255,255,255,0.06)',
                          gap: 6,
                        }}>
                          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                            {images.length > 0 ? `${images.length}/5 file${images.length > 1 ? 's' : ''} selected — click to add more` : 'Click to upload images or videos'}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Up to 5 files · Images · Videos (50MB each)</div>
                        </label>
                        <input id="file-upload" type="file" accept="image/*,video/*" multiple onChange={handleImageChange} style={{ display: 'none' }} />
                      </div>

                      {imagePreviews.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(90px,1fr))', gap: 10, marginBottom: 20 }}>
                          {imagePreviews.map((p, i) => (
                            <div key={i} style={{ position: 'relative' }}>
                              {p.type === 'video' ? (
                                <video src={p.src} style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8 }} muted />
                              ) : (
                                <img src={p.src} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8 }} />
                              )}
                              <button type="button" onClick={() => removeImage(i)}
                                style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: COLORS.red, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 12 }}>
                        <button type="button" className="ur-btn-hover" style={{ ...s.btnSecondary, flex: 1 }} onClick={() => { setStep(1); setError(''); }}>Back</button>
                        <button type="submit" disabled={loading} className="ur-btn-hover" style={{ ...s.btnPrimary, flex: 2, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                          {loading ? 'Submitting...' : 'Submit Complaint'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreateComplaint() {
  return (
    <ThemeProvider>
      <CreateComplaintInner />
    </ThemeProvider>
  );
}
